import { ThemeProvider } from "@sunday/component";
import { useTranslation } from "next-i18next";
import { ICTH_THEME } from "src/components/Common/ICTHLayout/constants";
import defaultPrivacyContent from "../../../../public/static/locales/en/privacy.json";
import {
  PrivacyArticle,
  PrivacyContent,
  PrivacyContentContainer,
  PrivacyPageContainer,
  PrivacyTitle,
} from "./PrivacyPolicy.style";

type PrivacyTextSegment = {
  text: string;
  href?: string;
};

type PrivacyTextContent = {
  text?: string;
  segments?: PrivacyTextSegment[];
};

type PrivacyParagraphBlock = PrivacyTextContent & {
  type: "paragraph";
};

type PrivacyListBlock = {
  type: "list";
  items: PrivacyTextContent[];
};

type PrivacySection = {
  title: string;
  blocks: Array<PrivacyParagraphBlock | PrivacyListBlock>;
};

const getTextContentKey = (content: PrivacyTextContent): string =>
  content.text || content.segments?.map(({ text }) => text).join("") || "";

const renderTextContent = (content: PrivacyTextContent): React.ReactNode => {
  if (content.segments) {
    return content.segments.map(({ text, href }) =>
      href ? (
        <a key={href} href={href} target="_blank" rel="noopener noreferrer">
          {text}
        </a>
      ) : (
        <span key={text}>{text}</span>
      )
    );
  }

  return content.text;
};

const PrivacyPolicy = (): JSX.Element => {
  const { i18n, t } = useTranslation("privacy");
  const translatedIntro = t("intro", { returnObjects: true });
  const translatedSections = t("sections", {
    returnObjects: true,
  });
  const intro = Array.isArray(translatedIntro)
    ? (translatedIntro as string[])
    : defaultPrivacyContent.intro;
  const sections = Array.isArray(translatedSections)
    ? (translatedSections as PrivacySection[])
    : (defaultPrivacyContent.sections as unknown as PrivacySection[]);

  return (
    <ThemeProvider theme={ICTH_THEME}>
      <PrivacyPageContainer>
        <PrivacyContentContainer>
          <PrivacyContent>
            <PrivacyTitle lang={i18n.language || "en"}>
              {t("title", { defaultValue: defaultPrivacyContent.title })}
            </PrivacyTitle>
            <PrivacyArticle>
              {intro.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}

              {sections.map((section) => (
                <section key={section.title}>
                  <h2>{section.title}</h2>
                  {section.blocks.map((block) => {
                    const blockKey =
                      block.type === "list"
                        ? `${section.title}-list`
                        : getTextContentKey(block);

                    if (block.type === "list") {
                      return (
                        <ul key={blockKey}>
                          {block.items.map((item) => (
                            <li key={getTextContentKey(item)}>
                              {renderTextContent(item)}
                            </li>
                          ))}
                        </ul>
                      );
                    }

                    return <p key={blockKey}>{renderTextContent(block)}</p>;
                  })}
                </section>
              ))}
            </PrivacyArticle>
          </PrivacyContent>
        </PrivacyContentContainer>
      </PrivacyPageContainer>
    </ThemeProvider>
  );
};

export default PrivacyPolicy;
